import { useEffect, useMemo, useState } from "react";
import {
  Order,
  OrderStatus,
  StatusHistoryEntry,
} from "@/redux/services/order/order.type";

const TERMINAL_STATUSES: OrderStatus[] = [
  "served",
  "picked_up",
  "delivered",
  "canceled",
  "rejected",
];

export interface StatusDuration {
  status: OrderStatus;
  durationMs: number;
  isCurrent: boolean;
}

function formatDuration(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  if (totalMinutes < 1) return "<1m";
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h${minutes}`;
}

function colorForCurrentDuration(ms: number): string {
  const totalMinutes = ms / 60000;
  if (totalMinutes < 10) return "text-green-600";
  if (totalMinutes < 20) return "text-yellow-600";
  return "text-red-600";
}

function buildDurations(
  createdAt: string,
  status: OrderStatus,
  statusHistory: StatusHistoryEntry[] | undefined,
  now: number
): StatusDuration[] {
  const createdAtMs = new Date(createdAt).getTime();
  const history = statusHistory ?? [];

  if (history.length === 0) {
    return [
      {
        status,
        durationMs: now - createdAtMs,
        isCurrent: true,
      },
    ];
  }

  const sorted = [...history].sort(
    (a, b) =>
      new Date(a.performedAt).getTime() - new Date(b.performedAt).getTime()
  );

  const durations: StatusDuration[] = [];
  let prevTime = createdAtMs;
  for (const entry of sorted) {
    const entryTime = new Date(entry.performedAt).getTime();
    durations.push({
      status: entry.fromStatus,
      durationMs: entryTime - prevTime,
      isCurrent: false,
    });
    prevTime = entryTime;
  }

  durations.push({
    status,
    durationMs: now - prevTime,
    isCurrent: true,
  });

  return durations;
}

export function useOrderTimingBreakdown(
  order: Order,
  updateInterval = 60000
) {
  const isTerminal = TERMINAL_STATUSES.includes(order.status);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (isTerminal) return;
    const tick = () => setNow(Date.now());
    const interval = setInterval(tick, updateInterval);
    return () => clearInterval(interval);
  }, [isTerminal, updateInterval]);

  return useMemo(() => {
    const durations = buildDurations(
      order.createdAt,
      order.status,
      order.statusHistory,
      isTerminal ? new Date(order.createdAt).getTime() + 0 : now
    );

    // For terminal statuses, the "current" duration is just the finished state
    // and we only want to show the past durations meaningfully.
    if (isTerminal) {
      // Remove synthetic current entry; keep only historical
      const historical = durations.slice(0, -1);
      return {
        durations: historical,
        isTerminal,
        currentColorClass: "",
      };
    }

    const currentDuration = durations[durations.length - 1];
    return {
      durations,
      isTerminal,
      currentColorClass: colorForCurrentDuration(currentDuration.durationMs),
    };
  }, [order.createdAt, order.status, order.statusHistory, now, isTerminal]);
}

export { formatDuration };
